import React, { useEffect, useState } from "react";
import { DatePicker, Table } from "antd";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { loggedInUser } from "core/redux/slice/authSlice";
import { useGetTimesheetDataQuery } from "AppQuries/attendance/timesheet/get-timesheet";
import { format } from "date-fns";
import { checkIsPublicHoliday } from "core/Operations/attendance/checkPublicHoliday";
import { useGetPublicHolidaysQuery } from "AppQuries/leave/public-holiday/public-holiday";
import LoadingSpinner from "Components/UI/LoadingSpinner";
import Error404 from "MainPages/ErrorPages/error404";
import TableTop from "Components/UI/TableTop";
import { useTableSearch } from "core/Operations/useTableSearch";
import MonthOption from "Components/Form/MonthSelect";
import YearOption from "Components/Form/YearSelect";
import { useGetEmployeeQuery } from "AppQuries/employee/employee_list";
import Select from "react-select";
import { all_routes } from "Router/all_routes";
import "../../style/css/timesheet.css";

const AttendanceTimesheet = () => {
  const userData = useSelector(loggedInUser);

  const [filter, setFilter] = useState({
    companyCode: userData.companyCode,
    locationCode: userData.selectedLocationCode,
    year: format(new Date(), "yyyy"),
    month: format(new Date(), "MM"),
    employeeCode: 0,
  });

  const {
    data: timesheetData,
    isLoading,
    isFetching,
    isError,
    error,
    refetch: getTimesheet,
  } = useGetTimesheetDataQuery({
    ...filter,
    userId: userData?.userID,
    isAllCompanies: userData?.isAllCompanies,
    selectedCompanyCode: userData?.selectedCompanyCode,
  });

  const { data: publicHolidays, isFetching: holidayFetching } =
    useGetPublicHolidaysQuery({
      companyCode: userData.companyCode,
    });

  const { data: employees, refetch: getEmployee } = useGetEmployeeQuery({
    companyCode: userData?.companyCode,
    locationCode: userData?.selectedLocationCode,
    userId: userData?.userID,
    isAllCompanies: userData?.isAllCompanies,
    selectedCompanyCode: userData?.selectedCompanyCode,
    departmentCode: 0,
    isAll: true,
  });

  const employeeOptions = Array.isArray(employees)
    ? employees
        ?.filter((d) => d.isActive)
        .map((o) => ({
          id: o.employeeCode,
          label: `${o.uniqueId}-${o.fullName}`,
          value: o.employeeCode,
        }))
    : [];

  const [daysInfo, setDaysInfo] = useState([]);
  const [attendanceFilterDate, setAttendanceFilterDate] = useState();
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };
  useEffect(() => {
    if (!isFetching && !holidayFetching && timesheetData) {
      const startDate = new Date(timesheetData.startDate);
      const daysInMonth = new Date(
        startDate.getFullYear(),
        startDate.getMonth() + 1,
        0,
      ).getDate();

      const daysArray = Array.from({ length: daysInMonth }, (_, index) => {
        const currentDate = new Date(
          startDate.getFullYear(),
          startDate.getMonth(),
          index + 1,
        );

        return {
          date: format(currentDate, "dd-MMM"),
          dayName: format(currentDate, "EEE"),
          isWeekEnd: format(currentDate, "EEE") === "Sun",
          isPublicHoliday: checkIsPublicHoliday(
            publicHolidays,
            format(currentDate, "yyyy-MM-dd"),
          ),
        };
      });

      setDaysInfo(daysArray);
    }
  }, [timesheetData, holidayFetching, isFetching]);

  const dataSource = timesheetData?.employeeMonthlyAttendance?.map(
    (employee) => {
      const row = {
        uniqueID: employee.uniqueID,
        key: employee.employeeCode,
        fullName: employee.fullName,
      };
      daysInfo.forEach((_, index) => {
        row[`day_${index + 1}`] = employee.timesheetData[index];
      });
      return row;
    },
  );

  const columns = [
    {
      title: "Emp Code",
      dataIndex: "uniqueID",
      key: "uniqueID",
    },
    {
      title: "Employee",
      dataIndex: "fullName",
      key: "fullName",
      fixed: "left",
      width: 200,
      render: (text, record) => (
        <Link
          to={`${all_routes.employee}${all_routes.employee_view}/${record.key}`}
        >
          {text}
        </Link>
      ),
    },
    ...daysInfo.map((day, index) => ({
      title: (
        <>
          {day.date} <br />
          <span>{day.dayName}</span>
        </>
      ),
      dataIndex: `day_${index + 1}`,
      key: `day_${index + 1}`,
      // className: day.isPublicHoliday
      //   ? "public-holiday"
      //   : day.isWeekEnd
      //   ? "weekend"
      //   : "",
      align: "center",
      render: (value) => {
        let attendanceValue = value?.split("~");

        if (attendanceValue && attendanceValue.length > 0) {
          if (attendanceValue[0] === "1.00") {
            return (
              <div className="present">
                <i className="fa fa-check text-white" />
              </div>
            );
          }
          if (attendanceValue[0] === "0.50") {
            return <div className="half-day" />;
          }
          if (attendanceValue[0] === "0") {
            if (attendanceValue[4] === "A") {
              return (
                <div className="annual-leave">
                  <i className="fa fa-close text-white" />
                </div>
              );
            } else if (attendanceValue[4] === "HR") {
              return (
                <div className="half-rest-day">
                  <i className="fa fa-close text-white" />
                </div>
              );
            } else if (attendanceValue[4] === "PH") {
              return (
                <div className="public-holiday">
                  <i className="fa fa-close text-white" />
                </div>
              );
            } else {
              return (
                <div className="absent">
                  <i className="fa fa-close text-white" />
                </div>
              );
            }
          }
        }

        return null;
      },
    })),
  ];
  const searchColumns = ["fullName"];
  const { searchValue, setSearchValue, filteredData } = useTableSearch(
    dataSource,
    searchColumns,
  );

  const updateQueryParams = (key, value) => {
    setFilter((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading || isFetching) {
    return <LoadingSpinner />;
  }

  if (isError) {
    return <Error404 error={error} />;
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Attendance Timesheet</h4>
            <h6>Manage Attendance Timesheet</h6>
          </div>
        </div>
        <div className="card table-list-card border-0">
          <div className="card-body">
            <TableTop
              onSearch={setSearchValue}
              toggleFilterVisibility={toggleFilterVisibility}
              isFilterVisible={isFilterVisible}
              showToggle={true}
            />
            <div
              className={`card${isFilterVisible ? " visible" : ""}`}
              id="filter_inputs"
              // style={{ display: isFilterVisible ? "block" : "none" }}
            >
              <div className="card-body pb-0">
                <div className="form-row row">
                  <div className="col-lg-3 col-sm-6 col-12 mb-3">
                    <div className="form-group">
                      <label className="form-label">Employee</label>
                      <Select
                        className="select"
                        options={employeeOptions}
                        placeholder="Select Employee"
                        value={
                          employeeOptions.find(
                            (option) => option.value === filter.employeeCode,
                          ) || null
                        }
                        onChange={(e) => {
                          updateQueryParams("employeeCode", e?.value || 0);
                        }}
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                      />
                    </div>
                  </div>

                  <div className="col-lg-3 col-sm-6 col-12 mb-3">
                    <label className="form-label">
                      Month<span className="asterisk">*</span>
                    </label>

                    <MonthOption
                      selectedMonth={filter.month}
                      monthSelectHandler={(e) =>
                        updateQueryParams("month", e)
                      }
                    />
                  </div>

                  <div className="col-lg-3 col-sm-6 col-12 mb-3">
                    <label className="form-label">
                      Year<span className="asterisk">*</span>
                    </label>

                    <YearOption
                      selectedYear={filter.year}
                      yearSelectHandler={(value) =>
                        updateQueryParams("year", value)
                      }
                    />
                  </div>
                  <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                    <div className="input-blocks">
                      <label className="form-label">&nbsp;</label>
                      <Link
                        className="btn btn-filters ms-auto"
                        onClick={() => getTimesheet()}
                      >
                        <i data-feather="search" className="feather-search" />
                        Filter
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="wordset">
              <ul style={{ display: "flex" }}>
                {/* <ReactTooltip place="top" type="dark" effect="solid" /> */}
                <li>
                  <div className="present " data-tip="Present">
                    <i className="fa fa-check text-white" />
                  </div>
                  Present
                </li>

                <li>
                  <div className="absent" data-tip="Absent">
                    <i className="fa fa-close text-white" />
                  </div>
                  Absent
                </li>

                <li>
                  <div className="half-day" data-tip="Half day"></div> Half Day
                </li>

                <li>
                  <div className="annual-leave">
                    <i className="fa fa-close text-white" />
                  </div>
                  Leave
                </li>

                <li>
                  <div className="half-rest-day">
                    <i className="fa fa-close text-white" />
                  </div>
                  Half Rest Day
                </li>
                <li>
                  <div className="public-holiday">
                    <i className="fa fa-close text-white" />
                  </div>
                  Public Holiday
                </li>
              </ul>
            </div>

            <Table
              columns={columns}
              dataSource={filteredData}
              // pagination={false}
              scroll={{ x: "max-content" }}
              bordered
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceTimesheet;
